import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useWhatsAppWebAccountStatus } from 'src/hooks/whatsapp/useWhatsAppWebAccountStatus.hook';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';

/**
 * Persistent, non-dismissible banner that appears at the top of every route
 * where it is mounted whenever the workspace's WhatsApp Web account is
 * banned by Meta. Mirrors the WapiDisconnectedBanner pattern so it can be
 * dropped as the first child of Dashboard / Pipe / Messenger and any
 * future page without leaking page-specific logic.
 *
 * Why only BANNED:
 *   The chat banner (NoConnectionBanner) and the dashboard modal already
 *   cover every other disconnect reason; surfacing them as a persistent
 *   top-of-page banner across all routes would be noisy. BANNED is the
 *   only state where the user truly cannot operate and needs to escalate
 *   to Meta support no matter where they navigate.
 *
 * Why no dismiss:
 *   A banned account is not something the user can fix with a click —
 *   hiding the banner risks the user forgetting and burning support time
 *   re-discovering the cause. Same call as WapiDisconnectedBanner.
 */
export default function WhatsAppWebBannedBanner() {
  const { t } = useTranslation('common/whatsappWebBannedBanner');
  const { currentWorkspaceId } = useWorkspaceContext();
  const { errorCode } = useWhatsAppWebAccountStatus({
    workspaceId: currentWorkspaceId,
  });

  if (errorCode !== 'BANNED') return null;

  return (
    <div className="flex items-start gap-3 w-full text-sm bg-red-50 border-l-4 border-red-400 rounded-r-md px-4 py-3 text-red-700">
      <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
      <div>
        <p className="font-semibold">{t('title')}</p>
        <p>{t('description')}</p>
      </div>
    </div>
  );
}
